import React from 'react'
import Menu, { MenuProps } from 'components/menu'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { useApplicationStore } from 'hooks/application'
import { updateTable } from 'components/spreadsheet/contexts/data/actions'
import useProject from 'hooks/useProject'
import { Colour, Copy, Delete, Sync, Restore, Link as LinkIcon, Pencil, Visible, Comment } from 'components/icons'
import Toggle from 'components/toggle'
import { MenuHeader, MenuItem, MenuLabel, MenuList, MenuSeparator } from 'components/menu/items'
import api from 'helpers/api'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'

interface SettingsMenuProps extends MenuProps {
  setDeleteTableModal: (open: boolean) => void
  setDuplicateTableModal: (open: boolean) => void

  setRelatedModal: (open: boolean) => void
  setViewDuplicatesModal: (open: boolean) => void
  /** Starts renaming the table where its name is shown. */
  onRenameTable?: () => void
}

const SettingsMenu: React.FC<SettingsMenuProps> = ({
  id,
  menuState,
  setMenuState,
  width,
  setDeleteTableModal,

  setDuplicateTableModal,

  setRelatedModal,
  setViewDuplicatesModal,
  onRenameTable
}) => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { project } = useProject()

  const handleUpdateSuccess = () => {
    setSnackbarMessage({ status: 'success', message: 'Save has been successful!' })
  }

  const handleUpdateFailure = (error: any) => {
    displayErrorMessage(error)
  }

  const restoreTable = () => {
    api.restoreTable(spreadsheetData.tableDetails.publicId, { context: { projectId: project.publicId } }).then(() => {
      window.location.reload()
    })
  }

  const isAdmin = spreadsheetData.isAdmin
  const isDeleted = spreadsheetData.tableDetails.isDeleted
  const canSeeRelated = isAdmin || spreadsheetData.tableDetails.permissionLevel === 4
  const update = (field: 'keepValidationsInSync' | 'keepColoursInSync' | 'allowComments', on: boolean) =>
    updateTable(
      field,
      spreadsheetData,
      setSpreadsheetData,
      on,
      project.publicId,
      handleUpdateSuccess,
      handleUpdateFailure
    )

  // A setting that's on or off: the row and its switch both flip it.
  const setting = (
    key: string,
    icon: React.ReactNode,
    label: string,
    name: string,
    on: boolean,
    field: Parameters<typeof update>[0]
  ) => (
    <MenuItem
      key={key}
      icon={icon}
      onClick={() => update(field, !on)}
      end={<Toggle aria-label={name} toggled={on} onToggle={(toggle) => update(field, toggle)} />}
    >
      {label}
    </MenuItem>
  )

  // The menu in groups, a line between each that has anything in it.
  const sections: React.ReactNode[][] = [
    [
      onRenameTable && !isDeleted && isAdmin && (
        <MenuItem
          key="rename"
          icon={<Pencil />}
          data-testid="rename-table"
          onClick={() => {
            setMenuState(INITIAL_CONTEXT_MENU_STATE)
            onRenameTable()
          }}
        >
          Rename table
        </MenuItem>
      ),
      canSeeRelated && (
        <MenuItem key="related" icon={<LinkIcon />} onClick={() => setRelatedModal(true)}>
          Related resources
        </MenuItem>
      )
    ],
    isAdmin
      ? [
          <MenuLabel key="sync-label">Sync across views</MenuLabel>,
          setting(
            'validations',
            <Sync />,
            'Column validations',
            'Sync column validations across views',
            spreadsheetData.tableDetails.keepValidationsInSync,
            'keepValidationsInSync'
          ),
          setting(
            'colours',
            <Colour />,
            'Conditional formatting',
            'Sync conditional formatting across views',
            spreadsheetData.tableDetails.keepColoursInSync,
            'keepColoursInSync'
          )
        ]
      : [],
    isAdmin
      ? [
          setting(
            'comments',
            <Comment />,
            'Allow comments',
            'Allow comments',
            spreadsheetData.tableDetails.allowComments,
            'allowComments'
          )
        ]
      : [],
    [
      !isDeleted && isAdmin && (
        <MenuItem key="duplicate" icon={<Copy />} onClick={() => setDuplicateTableModal(true)}>
          Duplicate table
        </MenuItem>
      ),
      !isDeleted && isAdmin && (
        <MenuItem key="duplicates" icon={<Visible />} onClick={() => setViewDuplicatesModal(true)}>
          View duplicated tables
        </MenuItem>
      )
    ],
    [
      !isDeleted && isAdmin && (
        <MenuItem key="archive" icon={<Delete />} variant="destructive" onClick={() => setDeleteTableModal(true)}>
          Archive table
        </MenuItem>
      ),
      isDeleted && canSeeRelated && (
        <MenuItem key="restore" icon={<Restore />} onClick={() => restoreTable()}>
          Restore table
        </MenuItem>
      )
    ]
  ]
  const shown = sections.map((rows) => rows.filter(Boolean)).filter((rows) => rows.length > 0)

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width} zIndex={3000}>
      <MenuHeader>Table settings</MenuHeader>
      <MenuList>
        {shown.map((rows, index) => (
          <React.Fragment key={index}>
            {index > 0 && <MenuSeparator />}
            {rows}
          </React.Fragment>
        ))}
      </MenuList>
    </Menu>
  )
}

export default SettingsMenu
